import Lara from '@primeng/themes/lara';
import Material from '@primeng/themes/material';
import { definePreset, usePreset } from '@primeng/themes';

/**
 * PrimeNG 17 shipped one static CSS file per theme. PrimeNG 20 builds themes at
 * runtime from presets, so the theme names stored in the layout config (and in
 * the backend user preferences) are mapped onto a preset, a primary color and a
 * color scheme instead.
 */
export const DARK_MODE_CLASS = 'app-dark';

type PresetFamily = 'lara' | 'material';

export interface ResolvedTheme {
  family: PresetFamily;
  primary: string;
  dark: boolean;
}

const DEFAULT_THEME: ResolvedTheme = {
  family: 'lara',
  primary: 'purple',
  dark: true,
};

const PRIMARY_BY_SUFFIX: Record<string, string> = {
  blue: 'blue',
  purple: 'purple',
  deeppurple: 'violet',
  indigo: 'indigo',
  teal: 'teal',
  green: 'green',
  orange: 'orange',
};

const DARK_PREFIXES = ['vela', 'arya'];

/** Maps a legacy PrimeNG 17 theme name (e.g. `bootstrap4-dark-purple`) to a PrimeNG 20 theme. */
export function resolveTheme(name: string | null | undefined): ResolvedTheme {
  if (!name) {
    return DEFAULT_THEME;
  }
  const parts = name.split('-');
  const prefix = parts[0];
  const suffix = parts[parts.length - 1];
  return {
    family: prefix === 'md' || prefix === 'mdc' ? 'material' : 'lara',
    primary: PRIMARY_BY_SUFFIX[suffix] ?? 'blue',
    dark: parts.includes('dark') || DARK_PREFIXES.includes(prefix),
  };
}

function primaryScale(color: string): Record<string, string> {
  const scale: Record<string, string> = {};
  [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950].forEach((step) => {
    scale[step] = `{${color}.${step}}`;
  });
  return scale;
}

export function buildPreset(theme: ResolvedTheme) {
  const base = theme.family === 'material' ? Material : Lara;
  return definePreset(base, {
    semantic: {
      primary: primaryScale(theme.primary),
    },
  });
}

export function applyColorScheme(dark: boolean): void {
  document.documentElement.classList.toggle(DARK_MODE_CLASS, dark);
}

/** Switches the active PrimeNG theme without a reload. */
export function applyTheme(name: string): void {
  const theme = resolveTheme(name);
  applyColorScheme(theme.dark);
  usePreset(buildPreset(theme));
}
